//src/components/EditProperty.jsx
import React from 'react';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi';
import { MdCheckCircle, MdEdit } from 'react-icons/md';
import { RiSaveLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { EditPropertyProvider, useEditProperty } from './EditPropertyContext';
import { ErrorAlert } from './SellPropertyUI';
import { STEPS } from './sellProperty.constants';
import { EditStep0OwnerInfo } from './steps/edit/EditStep0OwnerInfo';
import EditStep1PropertyDetail from './steps/edit/EditStep1PropertyDetail';
import EditStep2PriceSpecs from './steps/edit/EditStep2PriceSpecs';
import EditStep3Confirm from './steps/edit/EditStep3Confirm';

const STEP_COMPONENTS = [EditStep0OwnerInfo, EditStep1PropertyDetail, EditStep2PriceSpecs, EditStep3Confirm];

const SuccessScreen = ({ identifier }) => {
    const navigate = useNavigate();

    return (
        <div className="sell-success-page">
            <div className="sell-success-card">
                <div className="sell-success-icon">
                    <MdCheckCircle size={40} color="var(--color-success)" />
                </div>

                <h2 className="sell-success-title">Properti Berhasil Diperbarui!</h2>
                <p className="sell-success-desc">
                    Perubahan pada properti Anda telah disimpan. Tim kami akan meninjau kembali sebelum ditampilkan ke
                    publik.
                </p>

                <div className="sell-success-actions">
                    <button className="btn-primary-mp" onClick={() => navigate('/my-properties')}>
                        Lihat Properti Saya <HiArrowRight size={16} />
                    </button>
                    <button className="btn-outline-mp" onClick={() => navigate(`/dashboard/properties/${identifier}`)}>
                        Lihat Detail Properti
                    </button>
                </div>
            </div>
        </div>
    );
};


const StepIndicator = ({ step }) => (
    <div className="sell-step-indicator">
        {STEPS.map((label, i) => (
            <React.Fragment key={i}>
                <div className={`sell-step${i === step ? ' active' : i < step ? ' done' : ''}`}>
                    <div className="sell-step-circle">{i < step ? '✓' : i + 1}</div>
                    <span className="sell-step-label">{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`sell-step-line${i < step ? ' done' : ''}`} />}
            </React.Fragment>
        ))}
    </div>
);


const LoadingSkeleton = () => (
    <div className="sell-page">
        <div className="sell-page-header">
            <div className="container sell-page-header-inner">
                <div
                    style={{
                        height: 20,
                        width: 200,
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        marginBottom: 12,
                    }}
                />
                <div
                    style={{
                        height: 36,
                        width: 340,
                        background: 'rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        marginBottom: 8,
                    }}
                />
                <div style={{ height: 18, width: 260, background: 'rgba(255,255,255,0.15)', borderRadius: 8 }} />
            </div>
        </div>
        <div className="container sell-page-body">
            <div className="sell-form-card" style={{ padding: 32 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {[1, 2, 3, 4].map((i) => (
                        <div
                            key={i}
                            style={{ height: 48, background: 'var(--color-bg-soft)', borderRadius: 12, opacity: 0.6 }}
                        />
                    ))}
                </div>
            </div>
        </div>
    </div>
);


const EditPropertyInner = () => {
    const navigate = useNavigate();
    const {
        step,
        errors,
        loading,
        loadingProperty,
        submitted,
        property,
        identifier,
        nextStep,
        prevStep,
        handleSubmit,
    } = useEditProperty();

    if (loadingProperty) return <LoadingSkeleton />;
    if (submitted) return <SuccessScreen identifier={identifier} />;

    const ActiveStep = STEP_COMPONENTS[step];
    const isLastStep = step === STEPS.length - 1;

    return (
        <div className="sell-page">
            {/* Header */}
            <div className="sell-page-header">
                <div className="container sell-page-header-inner">
                    <p className="sell-page-eyebrow">
                        <MdEdit className="sell-page-eyebrow-icon" size={16} />
                        <span>Edit Properti</span>
                    </p>

                    <h1 className="sell-page-title">{property?.title ? `Edit: ${property.title}` : 'Edit Properti'}</h1>
                    <p className="sell-page-subtitle">
                        Perbarui informasi properti Anda. Perubahan akan ditinjau oleh tim sebelum ditampilkan.
                    </p>
                </div>
            </div>

            {/* Body */}
            <div className="container sell-page-body">
                <StepIndicator step={step} />

                <div className="sell-form-card">
                    <ErrorAlert message={errors.general} />
                    <ActiveStep />

                    <div className="sell-nav">
                        <button
                            type="button"
                            className="sell-nav-back"
                            onClick={step === 0 ? () => navigate('/my-properties') : prevStep}
                        >
                            <HiArrowLeft size={16} />
                            {step === 0 ? 'Batal' : 'Kembali'}
                        </button>

                        {!isLastStep ? (
                            <button type="button" className="btn-primary-mp" onClick={nextStep}>
                                Lanjut <HiArrowRight size={16} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn-primary-mp"
                                onClick={handleSubmit}
                                disabled={loading}
                                style={{ opacity: loading ? 0.7 : 1 }}
                            >
                                {loading ? (
                                    <>
                                        <span className="sell-spinner" /> Menyimpan...
                                    </>
                                ) : (
                                    <>
                                        <RiSaveLine size={17} /> Simpan Perubahan
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const EditProperty = () => (
    <EditPropertyProvider>
        <EditPropertyInner />
    </EditPropertyProvider>
);

export default EditProperty;
